import React from 'react';
import clsx from 'clsx';
import { Input } from '@/components/Input';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import DatePickerInput from '@/components/DatePickerInput';
import CustomAsyncSelect from '@/components/CustomAsyncSelect';
import CustomCheckbox from '@/components/CustomCheckbox';
import IconButton from '@/ctComponents/IconButton';
import { locationOpt } from '@/actions/Helper';
import { useSelector } from 'react-redux';
import { SaveApiResMessage } from '@/actions';
import { useHistory } from 'react-router-dom';

/**
 * SearchFilter - A reusable filter component for search functionality
 *
 * @param {Object} formData - The formData state object containing all filter values
 * @param {Function} setForm - Function to update formData state (e.g., (field, value) => setFormData({...formData, [field]: value}))
 * @param {Function} onSubmit - Callback function when search/add buttons are clicked
 * @param {Boolean} showAddButton - Whether to show the "Add" button (default: true)
 * @param {String} addButtonText - Text for the add button (default: "Add")
 * @param {String} searchButtonText - Text for the search button (default: "Search")
 * @param {String} className - Additional CSS classes for the container
 */

const SearchFilter = ({
  formData,
  setForm,
  onSubmit,
  showAddButton = true,
  addButtonText = 'Add',
  searchButtonText = 'Search',
  className = '',
  activeNotify,
  hasResetButton = false,
}) => {
  const selectOp = useSelector((state) => state.reducer);

  const history = useHistory();

  const reset = () => {
    history.push('/search');
  };

  return (
    <div
      className={`bg-gray-6000 rounded-lg p-4 mt-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 shrink-0 ${className}`}>
      <div>
        <CustomSelectEx
          height={40}
          dropdown
          isClearable
          placeholder="Container Size"
          className={clsx('w-full')}
          value={formData.specification_id}
          onChange={(item) => setForm('specification_id', item)}
          options={selectOp.size}
        />
      </div>
      <div>
        <CustomSelectEx
          height={40}
          dropdown
          isClearable
          placeholder="Container SSL"
          className={clsx('w-full')}
          value={formData.company_id}
          onChange={(item) => setForm('company_id', item)}
          options={selectOp.ssl}
        />
      </div>

      <div>
        <CustomAsyncSelect
          dropdown
          isClearable
          height={38}
          placeholder="Enter your Location"
          value={formData.loc}
          loadOptions={locationOpt}
          onChange={(item) => setForm('loc', item)}
          cacheOptions
          defaultOptions
        />
      </div>
      <div>
        <Input
          type="number"
          placeholder="Number"
          InputProps={{ disableUnderline: true }}
          inputProps={{ min: 1 }}
          value={formData.count}
          onChange={(ev) => setForm('count', ev.target.value)}
          height={40}
          backgroundColor="white"
        />
      </div>

      <div>
        <DatePickerInput
          placeholder="Date"
          format="MM/dd/yyyy"
          value={formData.date}
          onChange={(item) => setForm('date', item)}
          height={40}
        />
      </div>

      <div className="flex items-center gap-4">
        <CustomCheckbox
          color="secondary"
          labelClass="text-white text-sm whitespace-nowrap"
          checked={formData.chassis.own}
          onChange={() => {
            const newOwnValue = !formData.chassis.own;
            const newPoolValue = formData.chassis.pool;

            // If trying to uncheck own and pool is already false, prevent the action
            if (!newOwnValue && !newPoolValue) {
              // Show notification that at least one must be checked

              SaveApiResMessage({
                title: null,
                message:
                  'At least one chassis option (Own or Pool) must be selected',
                mode: 'warning',
              });
              return;
            }

            setForm('chassis', {
              ...formData.chassis,
              own: newOwnValue,
            });
          }}
          label="Own Chassis"
        />
        <CustomCheckbox
          color="secondary"
          labelClass="text-white text-sm whitespace-nowrap"
          checked={formData.chassis.pool}
          onChange={() => {
            const newPoolValue = !formData.chassis.pool;
            const newOwnValue = formData.chassis.own;

            // If trying to uncheck pool and own is already false, prevent the action
            if (!newPoolValue && !newOwnValue) {
              // Show notification that at least one must be checked
              SaveApiResMessage({
                title: null,
                message:
                  'At least one chassis option (Own or Pool) must be selected',
                mode: 'warning',
              });
              return;
            }

            setForm('chassis', {
              ...formData.chassis,
              pool: newPoolValue,
            });
          }}
          label="Pool Chassis"
        />
      </div>

      <div
        className={`gap-2 flex justify-end items-center ${
          hasResetButton
            ? 'lg:col-start-4 lg:col-end-6 sm:col-start-3 xl:col-start-5 xl:col-end-6'
            : 'lg:col-start-5 sm:col-start-3'
        }`}>
        {hasResetButton && (
          <IconButton
            color="#525866"
            style={{
              backgroundColor: '#FFF',
              height: 40,
              width: 110,
            }}
            text="Reset"
            onClick={reset}
          />
        )}
        <IconButton
          icon="searchIcon"
          text={searchButtonText}
          onClick={onSubmit}
          color="#131617"
          style={{
            backgroundColor: '#FFAD00',
            height: 40,
            width: 110,
            borderColor: '#FFAD00',
          }}
        />
        {showAddButton && (
          <IconButton
            icon="list"
            text={addButtonText}
            onClick={activeNotify}
            color="#525866"
            style={{
              backgroundColor: '#FFF',
              height: 40,
              width: 110,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SearchFilter;
